import { useCallback, useMemo } from "react";
import type { CustomNewRelicMicroAgent } from "../CustomNewRelicMicroAgent";
import { shouldIgnoreError } from "../shouldIgnoreError";

let lazyLoadedNewRelicMicroAgentRequested = false;
let lazyLoadedNewRelicMicroAgent: CustomNewRelicMicroAgent | undefined;
const errorQueue: Parameters<UseNewRelicAgentReturn["logErrorToNewRelic"]>[] =
    [];
const messageQueue: Parameters<
    UseNewRelicAgentReturn["logMessageToNewRelic"]
>[] = [];

type UseNewRelicAgentReturn = {
    logErrorToNewRelic: (
        error: Error | string,
        customAttributes?: Record<string, unknown>,
    ) => void;
    logMessageToNewRelic: (
        message: string,
        options?: {
            level?: "ERROR" | "TRACE" | "DEBUG" | "INFO" | "WARN";
            customAttributes?: Record<string, unknown>;
        },
    ) => void;
};

export function useNewRelicAgent(): UseNewRelicAgentReturn {
    const logErrorToNewRelic = useCallback<
        UseNewRelicAgentReturn["logErrorToNewRelic"]
    >((error, customAttributes) => {
        if (import.meta.env.DEV) {
            console.error(error, customAttributes);

            return;
        }

        if (shouldIgnoreError(error)) return;

        // Test existing new relic agent in page for logging errors support. If there's none available, lazy load one.
        const agentToUse =
            window.newrelic?.noticeError !== undefined
                ? window.newrelic
                : lazyLoadedNewRelicMicroAgent;

        if (agentToUse === undefined) {
            if (!lazyLoadedNewRelicMicroAgentRequested) {
                lazyLoadedNewRelicMicroAgentRequested = true;

                void import("../CustomNewRelicMicroAgent").then((module) => {
                    lazyLoadedNewRelicMicroAgent =
                        new module.CustomNewRelicMicroAgent([
                            "jserrors",
                            "logging",
                        ]);

                    errorQueue.forEach((queueItem) => {
                        lazyLoadedNewRelicMicroAgent?.noticeError(
                            queueItem[0],
                            queueItem[1],
                        );
                    });

                    errorQueue.length = 0;
                    lazyLoadedNewRelicMicroAgentRequested = false;
                });
            }

            errorQueue.push([error, customAttributes]);
        } else {
            agentToUse.noticeError(error, customAttributes);
        }
    }, []);

    const logMessageToNewRelic = useCallback<
        UseNewRelicAgentReturn["logMessageToNewRelic"]
    >((message, options) => {
        if (import.meta.env.DEV) {
            console.info(message, options);

            return;
        }

        // Test existing new relic agent in page for logging messages support. If there's none available, lazy load one.
        const agentToUse =
            window.newrelic?.log !== undefined
                ? window.newrelic
                : lazyLoadedNewRelicMicroAgent;

        if (agentToUse === undefined) {
            if (!lazyLoadedNewRelicMicroAgentRequested) {
                lazyLoadedNewRelicMicroAgentRequested = true;
                void import("../CustomNewRelicMicroAgent").then((module) => {
                    lazyLoadedNewRelicMicroAgent =
                        new module.CustomNewRelicMicroAgent([
                            "jserrors",
                            "logging",
                        ]);

                    messageQueue.forEach((queueItem) => {
                        lazyLoadedNewRelicMicroAgent?.log(
                            queueItem[0],
                            queueItem[1],
                        );
                    });

                    messageQueue.length = 0;
                    lazyLoadedNewRelicMicroAgentRequested = false;
                });
            }

            messageQueue.push([message, options]);
        } else {
            agentToUse.log(message, options);
        }
    }, []);

    return useMemo(
        () => ({
            logErrorToNewRelic,
            logMessageToNewRelic,
        }),
        [logErrorToNewRelic, logMessageToNewRelic],
    );
}
